import { useMutation } from '@tanstack/react-query'
import { getRouteApi, useRouter } from '@tanstack/react-router'
import { Request } from 'commons/utils/request'
import { ButtonGroup, Link, Toast } from 'ui/components'
import { Button } from 'ui/components/Buttons'
import { HeadingTitle } from 'ui/components/HeadingTitle'
import { Info, InfoSet, InfoSetDirection } from 'ui/components/Info'
import { PageSection, PageSectionFace } from 'ui/components/Page'
import { Col, Row } from 'ui/themes/globalStyles'
import { AnnualReviewStatusLabel } from '~bank-annual-review/components/AnnualReviewStatusLabel'
import { AnnualReviewDetails } from '~bank-annual-review/types/AnnualReviewDetails'

import { handlerServerError } from '@orbit/errors'

const routeApi = getRouteApi('/_app/annual-review/detail/$companyId/$id')

type MappedOverviewFromResponse<T> = {
  [K in keyof T as K extends `overview${string}` ? K : never]: T[K]
}

export const Overview = () => {
  const router = useRouter()
  const { id, companyId } = routeApi.useParams()
  const loaderData = routeApi.useLoaderData() as MappedOverviewFromResponse<AnnualReviewDetails> &
    Pick<AnnualReviewDetails, 'canBeApproved' | 'isNew' | 'editButtonText'>

  const {
    overviewFinancialsEndDate,
    overviewFinancialsStartDate,
    overviewFinancialsStartDateLabel,
    overviewFinancialsEndDateLabel,
    overviewLegalName,
    overviewRelatedCompanies,
    overviewRelationshipName,
    overviewStatus,
    overviewDba,
    overviewLastARDate,
    canBeApproved,
    editButtonText
  } = loaderData

  const { mutate: approve } = useMutation<void, Error, { id: number }>({
    mutationFn: ({ id }) => Request.put(Request.urls.bank.annualReviewApprove, { id }),
    onSuccess: () => {
      Toast.successSmall('Annual Review was successfully approved!')
      router.invalidate()
    },
    onError: (e) => handlerServerError(e)
  })

  const onApprove = () => approve({ id: Number(id) })

  return (
    <PageSection
      first
      title="Annual Review"
      face={PageSectionFace.THIRD}
      actions={
        <ButtonGroup margin="big">
          <Link to="/annual-review/edit/$companyId/$id" params={{ companyId, id }}>
            <Button face={editButtonText.toLocaleLowerCase() === 'edit' ? 'secondary' : 'primary'}>
              {editButtonText}
            </Button>
          </Link>
          {canBeApproved && (
            <Button face="primary" onClick={onApprove}>
              Approve
            </Button>
          )}
        </ButtonGroup>
      }
    >
      <HeadingTitle large={true}>Overview</HeadingTitle>
      <InfoSet direction={InfoSetDirection.ROW}>
        <Row>
          <Col>
            <Info label="Relationship Name">
              <Link to="/relationships/$id" params={{ id: companyId.toString() }}>
                {overviewRelationshipName}
              </Link>
            </Info>
          </Col>
          <Col>
            <Info label="DBA (if applicable)">{overviewDba}</Info>
          </Col>
        </Row>

        <Row>
          <Col>
            <Info label="Licensed Entity Legal Name">{overviewLegalName}</Info>
          </Col>
          <Col>
            <Info label="Related Companies">
              {overviewRelatedCompanies ? overviewRelatedCompanies.join(', ') : '———'}
            </Info>
          </Col>
        </Row>

        <Row>
          <Col>
            <Info label="Date of Last Annual Review">{overviewLastARDate || '---'}</Info>
          </Col>
          <Col>
            <Info label="Status">
              <AnnualReviewStatusLabel status={overviewStatus} />
            </Info>
          </Col>
        </Row>

        <Row>
          <Col>
            <Info label={overviewFinancialsStartDateLabel}>{overviewFinancialsStartDate || '---'}</Info>
          </Col>
          <Col />
        </Row>

        <Row>
          <Col>
            <Info label={overviewFinancialsEndDateLabel}>{overviewFinancialsEndDate || '---'}</Info>
          </Col>
          <Col />
        </Row>
      </InfoSet>
    </PageSection>
  )
}
